import * as React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import EditIcon from '@mui/icons-material/Edit';
import Divider from '@mui/material/Divider';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PrintIcon from '@mui/icons-material/Print';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { IconButton } from '@mui/material';
import { OrderStatus, SpecializedOrderStatus } from '../../api/ClientApi';
import { CompleteOrderType } from '../../common/Types';
import { useAppSelector } from '../../hooks/hooks';
import {
    isEmployeeListed,
    isEmployeeProjectManager,
    isEmployeeWarehouseManager,
    isEmployeeWarehouseOwner,
} from '../../redux/selectors/employeeSelector';

type OrdersTableMenuProps = {
    goToOrderForm(): void;
    goToAcceptOrderForm(): void;
    goToReceiptForm(): void;
    goToWaybillForm(): void;
    goToCancelOrderForm(): void;
    acceptDemandOrder(): void;
    acceptReturnOrder(): void;
    goToDemandOrderForm(): void;
    goToReturnOrderForm(): void;
    goToCloseOrderForm(): void;
    goToPrintWaybill(): void;
    rejectDelivery(): void;
    menuOrderStatus?: OrderStatus;
    menuSpecializedOrderStatus?: SpecializedOrderStatus;
    orderType?: CompleteOrderType;
    originSiteOwners?: string[];
    originSiteProjectManagers?: string[];
    destinationSiteOwners?: string[];
};

export default function OrdersTableMenu(props: OrdersTableMenuProps) {
    const {
        goToOrderForm,
        goToAcceptOrderForm,
        goToReceiptForm,
        goToWaybillForm,
        goToCancelOrderForm,
        acceptDemandOrder,
        acceptReturnOrder,
        goToDemandOrderForm,
        goToReturnOrderForm,
        goToCloseOrderForm,
        goToPrintWaybill,
        rejectDelivery,
        menuOrderStatus,
        menuSpecializedOrderStatus,
        orderType,
        originSiteOwners,
        originSiteProjectManagers,
        destinationSiteOwners,
    } = props;

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const isWarehouseOwner = useAppSelector(isEmployeeWarehouseOwner);
    const isWarehouseManager = useAppSelector(isEmployeeWarehouseManager);
    const isProjectManager = useAppSelector(isEmployeeProjectManager);

    const isEmployeeOriginSiteOwner = useAppSelector(isEmployeeListed(originSiteOwners ?? []));
    const isEmployeeOriginSiteProjectManager = useAppSelector(isEmployeeListed(originSiteProjectManagers ?? []));
    const isEmployeeDestinationSiteOwner = useAppSelector(isEmployeeListed(destinationSiteOwners ?? []));

    const shouldDividerBeThere =
        ((isProjectManager || isWarehouseManager) &&
            (orderType === CompleteOrderType.Demand || orderType === CompleteOrderType.GeneralReturn)) ||
        (isWarehouseOwner &&
            (((menuOrderStatus === OrderStatus.Idle ||
                menuOrderStatus === OrderStatus.Pending ||
                menuOrderStatus === OrderStatus.Approved) &&
                isEmployeeOriginSiteOwner) ||
                (menuOrderStatus === OrderStatus.Transport && isEmployeeDestinationSiteOwner)));

    return (
        <div>
            <IconButton color="primary" onClick={handleClick} sx={{ m: 0 }}>
                <MoreVertIcon />
            </IconButton>
            <Menu
                id="demo-customized-menu"
                MenuListProps={{
                    'aria-labelledby': 'demo-customized-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <MenuItem
                    onClick={() =>
                        orderType === CompleteOrderType.Demand
                            ? goToDemandOrderForm()
                            : orderType === CompleteOrderType.GeneralReturn
                              ? goToReturnOrderForm()
                              : goToOrderForm()
                    }
                    disableRipple
                    disabled={
                        (orderType !== CompleteOrderType.Demand &&
                            orderType !== CompleteOrderType.GeneralReturn &&
                            menuOrderStatus !== OrderStatus.Idle &&
                            menuOrderStatus !== OrderStatus.Pending) ||
                        (!!menuSpecializedOrderStatus &&
                            menuSpecializedOrderStatus !== SpecializedOrderStatus.Created &&
                            menuSpecializedOrderStatus !== SpecializedOrderStatus.InProgress)
                    }
                >
                    <EditIcon color="primary" />
                    {orderType === CompleteOrderType.Demand
                        ? 'Izmeni trebovanje'
                        : orderType === CompleteOrderType.GeneralReturn
                          ? 'Izmeni povratnicu'
                          : 'Izmeni nalog'}
                </MenuItem>
                {(isProjectManager || isWarehouseManager) && (
                    <MenuItem
                        onClick={() => goToCancelOrderForm()}
                        disableRipple
                        disabled={
                            menuOrderStatus === OrderStatus.Cancelled ||
                            menuOrderStatus === OrderStatus.Returned ||
                            menuOrderStatus === OrderStatus.Delivered ||
                            (!!menuSpecializedOrderStatus &&
                                menuSpecializedOrderStatus !== SpecializedOrderStatus.Created &&
                                menuSpecializedOrderStatus !== SpecializedOrderStatus.InProgress)
                        }
                    >
                        <CloseIcon color="error" />
                        {orderType === CompleteOrderType.Demand
                            ? 'Otkaži trebovanje'
                            : orderType === CompleteOrderType.GeneralReturn
                              ? 'Otkaži povratnicu'
                              : 'Otkaži nalog'}
                    </MenuItem>
                )}
                {shouldDividerBeThere && <Divider sx={{ my: 0.5 }} />}
                {orderType === CompleteOrderType.Demand && (isProjectManager || isWarehouseManager) && (
                    <MenuItem
                        onClick={() => acceptDemandOrder()}
                        disableRipple
                        disabled={
                            orderType !== CompleteOrderType.Demand ||
                            (menuSpecializedOrderStatus !== SpecializedOrderStatus.Created &&
                                menuSpecializedOrderStatus !== SpecializedOrderStatus.InProgress)
                        }
                    >
                        <CheckIcon color="success" />
                        Kreiraj nalog
                    </MenuItem>
                )}
                {orderType === CompleteOrderType.GeneralReturn && (isProjectManager || isWarehouseManager) && (
                    <MenuItem
                        onClick={() => acceptReturnOrder()}
                        disableRipple
                        disabled={
                            orderType !== CompleteOrderType.GeneralReturn ||
                            (menuSpecializedOrderStatus !== SpecializedOrderStatus.Created &&
                                menuSpecializedOrderStatus !== SpecializedOrderStatus.InProgress)
                        }
                    >
                        <CheckIcon color="success" />
                        Kreiraj nalog
                    </MenuItem>
                )}
                {orderType === CompleteOrderType.Demand &&
                    (isProjectManager || isWarehouseManager) &&
                    menuSpecializedOrderStatus === SpecializedOrderStatus.InProgress && (
                        <MenuItem
                            onClick={() => goToCloseOrderForm()}
                            disableRipple
                            disabled={orderType !== CompleteOrderType.Demand}
                        >
                            <DoneAllIcon color="success" />
                            Zatvori nalog
                        </MenuItem>
                    )}
                {orderType === CompleteOrderType.GeneralReturn &&
                    (isProjectManager || isWarehouseManager) &&
                    menuSpecializedOrderStatus === SpecializedOrderStatus.InProgress && (
                        <MenuItem
                            onClick={() => goToCloseOrderForm()}
                            disableRipple
                            disabled={orderType !== CompleteOrderType.GeneralReturn}
                        >
                            <DoneAllIcon color="success" />
                            Zatvori nalog
                        </MenuItem>
                    )}
                {menuOrderStatus === OrderStatus.Idle && isProjectManager && isEmployeeOriginSiteProjectManager && (
                    <MenuItem
                        onClick={() => goToAcceptOrderForm()}
                        disableRipple
                        disabled={menuOrderStatus !== OrderStatus.Idle}
                    >
                        <CheckIcon color="success" />
                        Prihvati nalog
                    </MenuItem>
                )}
                {menuOrderStatus === OrderStatus.Pending && isWarehouseOwner && isEmployeeOriginSiteOwner && (
                    <MenuItem
                        onClick={() => goToReceiptForm()}
                        disableRipple
                        disabled={menuOrderStatus !== OrderStatus.Pending}
                    >
                        <CheckIcon color="success" />
                        Prihvati nalog
                    </MenuItem>
                )}
                {menuOrderStatus === OrderStatus.Approved && isWarehouseOwner && isEmployeeOriginSiteOwner && (
                    <MenuItem
                        onClick={() => goToWaybillForm()}
                        disableRipple
                        disabled={menuOrderStatus !== OrderStatus.Approved}
                    >
                        <LocalShippingIcon color="primary" />
                        Pošalji nalog
                    </MenuItem>
                )}
                {menuOrderStatus === OrderStatus.Transport && isWarehouseOwner && isEmployeeDestinationSiteOwner && (
                    <MenuItem
                        onClick={() => goToReceiptForm()}
                        disableRipple
                        disabled={menuOrderStatus !== OrderStatus.Transport}
                    >
                        <DownloadIcon color="warning" />
                        Preuzmi robu
                    </MenuItem>
                )}
                {menuOrderStatus === OrderStatus.Transport && isWarehouseOwner && isEmployeeDestinationSiteOwner && (
                    <MenuItem onClick={() => rejectDelivery()} disableRipple>
                        <ReportProblemIcon color="error" />
                        Nisam primio robu
                    </MenuItem>
                )}
                {menuOrderStatus === OrderStatus.Transport && isWarehouseOwner && isEmployeeOriginSiteOwner && (
                    <MenuItem
                        onClick={() => goToPrintWaybill()}
                        disableRipple
                        disabled={menuOrderStatus !== OrderStatus.Transport}
                    >
                        <PrintIcon color="info" />
                        Odštampaj otpremnicu
                    </MenuItem>
                )}
            </Menu>
        </div>
    );
}
